import { Loader2, MessageSquarePlus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { useLlmChatSessionsForProduct } from '@/shared/hooks/api/llmChat'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
} from '@/components/ui/empty'
import {
  llmChatNewProductChatPath,
  llmChatSessionPath,
} from '@/features/llm-chat/llmChatPaths'
import { llmChatSessionListLabel } from '@/features/llm-chat/llmChatSessionLabels'
import type { ProductChatUnavailableReason } from '@/features/product-catalog/utils/productChatAvailability'

type ProductLinkedChatsSectionProps = {
  productId: string
  disableCreation?: boolean
  chatUnavailableReason?: ProductChatUnavailableReason | null
}

function getChatUnavailableMessageKey(
  reason: ProductChatUnavailableReason
): string {
  if (reason === 'failed') {
    return 'detail.chatsStartDisabledFailed'
  }
  if (reason === 'processing') {
    return 'detail.chatsStartDisabledProcessing'
  }
  return 'detail.chatsStartDisabledUnavailable'
}

function ProductChatStartButton({
  productId,
  labelKey,
  disabled,
}: {
  productId: string
  labelKey: 'detail.chatsStartCta' | 'detail.chatsStartNewCta'
  disabled: boolean
}) {
  const { t } = useTranslation('productCatalog')

  if (disabled) {
    return (
      <Button size='sm' disabled>
        <MessageSquarePlus className='size-4' aria-hidden />
        {t(labelKey)}
      </Button>
    )
  }

  return (
    <Button asChild size='sm'>
      <Link to={llmChatNewProductChatPath(productId)}>
        <MessageSquarePlus className='size-4' aria-hidden />
        {t(labelKey)}
      </Link>
    </Button>
  )
}

export function ProductLinkedChatsSection({
  productId,
  disableCreation = false,
  chatUnavailableReason = null,
}: ProductLinkedChatsSectionProps) {
  const { t } = useTranslation('productCatalog')
  const { data, isError, isLoading } = useLlmChatSessionsForProduct(productId)
  const sessions = data?.sessions ?? []
  let emptyDescription = t('detail.chatsEmpty')
  if (disableCreation && chatUnavailableReason != null) {
    emptyDescription = t(getChatUnavailableMessageKey(chatUnavailableReason))
  }

  return (
    <Card className='flex min-h-[280px] min-w-0 flex-col'>
      <CardHeader className='flex flex-row flex-wrap items-start justify-between gap-2 space-y-0 pb-4'>
        <CardTitle>{t('detail.chatsTitle')}</CardTitle>
        {sessions.length > 0 ? (
          <ProductChatStartButton
            productId={productId}
            labelKey='detail.chatsStartNewCta'
            disabled={disableCreation}
          />
        ) : null}
      </CardHeader>
      <CardContent className='flex flex-1 flex-col'>
        {isLoading ? (
          <div className='flex flex-1 items-center justify-center py-8'>
            <Loader2 className='size-6 animate-spin text-muted-foreground' />
          </div>
        ) : isError ? (
          <Empty className='flex-1 border-0 py-2'>
            <EmptyHeader>
              <EmptyDescription>{t('detail.chatsLoadError')}</EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : sessions.length === 0 ? (
          <Empty className='flex-1 border-0 py-2'>
            <EmptyHeader>
              <EmptyDescription>{emptyDescription}</EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <ProductChatStartButton
                productId={productId}
                labelKey='detail.chatsStartCta'
                disabled={disableCreation}
              />
            </EmptyContent>
          </Empty>
        ) : (
          <div className='grid gap-3 sm:grid-cols-2 xl:grid-cols-3'>
            {sessions.map((session) => {
              const { primary, secondary } = llmChatSessionListLabel(
                session,
                t('detail.chatsUntitled')
              )

              return (
                <Link
                  key={session.id}
                  to={llmChatSessionPath(session.id)}
                  aria-label={t('detail.chatsOpenAriaLabel', {
                    title: primary,
                  })}
                  className='min-w-0 rounded-lg border bg-background p-3 text-left transition-colors hover:bg-muted/40 focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none'
                >
                  <span className='line-clamp-2 text-sm leading-snug font-medium'>
                    {primary}
                  </span>
                  <span className='mt-1 block text-xs text-muted-foreground'>
                    {secondary}
                  </span>
                </Link>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
